import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AdminPageShell from "./AdminPageShell";
import useFetch from "../../useFetch";
import { apiRequest } from "../../utils/api";
import Pagination from "../Pagination";

const formatDate = (date) => date ? new Date(date).toLocaleDateString() : "Open until filled";
const formatDateTime = (date) => date ? new Date(date).toLocaleString() : "Not set";

const formatSalary = (job) => {
  if (job?.salary_range) return job.salary_range;
  if (job?.salary_min && job?.salary_max) return `${job.salary_currency ?? "USD"} ${job.salary_min} - ${job.salary_max}`;
  if (job?.salary_min) return `From ${job.salary_currency ?? "USD"} ${job.salary_min}`;
  if (job?.salary_max) return `Up to ${job.salary_currency ?? "USD"} ${job.salary_max}`;

  return "Not disclosed";
};

const formatList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean).join(", ");
  return value || "Not provided";
};

const statusOptions = [
  { value: "", label: "All Jobs" },
  { value: "pending_admin_review", label: "Pending Review" },
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "closed", label: "Closed" },
  { value: "declined", label: "Declined" },
];

const statusLabels = {
  pending_admin_review: "Pending Review",
  pending_verification: "Pending Verification",
};

const getStatusClass = (status) => {
  if (["closed", "declined", "expired"].includes(status)) return "danger";
  if (["pending_admin_review", "pending_verification", "paused"].includes(status)) return "warning";

  return "";
};

const AdminJobs = () => {
  const perPage = 10;
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get("status") ?? "";
  const [page, setPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);
  const [jobs, setJobs] = useState([]);
  const [meta, setMeta] = useState({});
  const [message, setMessage] = useState(false);
  const [actionError, setActionError] = useState(false);
  const [reviewJob, setReviewJob] = useState(null);
  const apiUrl = process.env.REACT_APP_BASE_API_URL
    + `/admin/jobs?per_page=${perPage}&page=${page}`
    + (statusFilter ? `&status=${encodeURIComponent(statusFilter)}` : "")
    + (refreshKey ? `&_=${refreshKey}` : "");
  const { response, isLoading, error } = useFetch(apiUrl);

  useEffect(() => {
    setJobs(response?.jobs?.data ?? []);
    setMeta(response?.jobs?.meta ?? {});
  }, [response]);

  const refresh = () => setRefreshKey(Date.now());

  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  const updateStatus = (job, status) => {
    const actionLabel = status === "closed"
      ? "Block"
      : status === "declined"
        ? "Decline"
        : job.status === "pending_admin_review"
          ? "Approve"
          : "Reopen";

    if (!window.confirm(`${actionLabel} this job?`)) return;

    setMessage(false);
    setActionError(false);
    const formData = new FormData();
    formData.append("status", status);

    apiRequest(`/admin/jobs/${job.id}/status`, { method: "POST", body: formData })
      .then((data) => {
        setMessage(data.message);
        setReviewJob(null);
        refresh();
      })
      .catch((error) => setActionError(error.message));
  };

  const handleStatusFilter = (event) => {
    const status = event.target.value;
    setSearchParams(status ? { status } : {});
  };

  const removeJob = (job) => {
    if (!window.confirm("Remove this job permanently from admin listing?")) return;

    setMessage(false);
    setActionError(false);
    apiRequest(`/admin/jobs/${job.id}`, { method: "DELETE" })
      .then((data) => {
        setMessage(data.message);
        setReviewJob(null);
        refresh();
      })
      .catch((error) => setActionError(error.message));
  };

  const renderReviewActions = (job) => (
    <>
      <Link to={`/admin-jobs/${job.id}/applicants`} className="theme-btn btn-style-three">Applicants</Link>
      <button type="button" className="theme-btn btn-style-danger" onClick={() => removeJob(job)}>Delete</button>
      {job.status === "pending_admin_review" ? (
        <>
          <button type="button" className="theme-btn btn-style-success" onClick={() => updateStatus(job, "active")}>Approve</button>
          <button type="button" className="theme-btn btn-style-danger" onClick={() => updateStatus(job, "declined")}>Decline</button>
        </>
      ) : job.status === "expired" || job.status === "declined" ? null : job.status === "closed" ? (
        <button type="button" className="theme-btn btn-style-success" onClick={() => updateStatus(job, "active")}>Reopen</button>
      ) : (
        <button type="button" className="theme-btn btn-style-danger" onClick={() => updateStatus(job, "closed")}>Block</button>
      )}
    </>
  );

  const reviewSections = reviewJob ? [
    { title: "Description", body: reviewJob.description },
    { title: "Responsibilities", body: reviewJob.responsibilities },
    { title: "Requirements", body: reviewJob.requirements },
    { title: "Benefits", body: reviewJob.benefits },
  ].filter((section) => section.body) : [];

  return (
    <AdminPageShell
      active="jobs"
      title="Manage Jobs"
      text="Review platform job listings and expiry status."
    >
      <div className="ls-widget">
        <div className="widget-title">
          <h4>All Jobs</h4>
        </div>
        <div className="admin-crud-bar">
          <p>Super admin can review, approve, decline, block, reopen, or remove job listings.</p>
          <div className="admin-crud-control">
            <label htmlFor="admin-job-status">Status</label>
            <select id="admin-job-status" value={statusFilter} onChange={handleStatusFilter}>
              {statusOptions.map((option) => (
                <option key={option.value || "all"} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="widget-content">
          {message && <div className="alert alert-success"><strong>{message}</strong></div>}
          {actionError && <div className="alert alert-danger"><strong>{actionError}</strong></div>}
          {error && <div className="alert alert-danger"><strong>{error}</strong></div>}
          {isLoading && <h5 className="text-center my-3">Loading Data</h5>}
          {!error && !isLoading && meta?.total > 0 && (
            <div className="admin-list-summary">Showing {jobs.length} of {meta.total} jobs</div>
          )}
          <div className="admin-table-list">
            {!error && !isLoading && jobs.map((job) => (
              <div className="admin-table-row" key={job.id}>
                <div>
                  <strong>{job.title}</strong>
                  <span>{job.company_name} - {job.applications_count} applicants - {formatDate(job.application_deadline)}</span>
                </div>
                <div className="admin-row-actions">
                  <span className={getStatusClass(job.status)}>{statusLabels[job.status] ?? job.status}</span>
                  <button type="button" className="theme-btn btn-style-three" onClick={() => setReviewJob(job)}>Review</button>
                </div>
              </div>
            ))}
            {!error && !isLoading && jobs.length === 0 && <div className="admin-empty-state">No jobs found.</div>}
          </div>
          {!error && !isLoading && <Pagination meta={meta} onPageChange={setPage} label="Admin jobs pagination" />}
        </div>
      </div>

      {reviewJob && (
        <div className="admin-review-modal" role="dialog" aria-modal="true" aria-labelledby="admin-job-review-title">
          <div className="admin-review-backdrop" onClick={() => setReviewJob(null)}></div>
          <div className="admin-review-panel">
            <div className="admin-review-head">
              <div>
                <span className={getStatusClass(reviewJob.status)}>{statusLabels[reviewJob.status] ?? reviewJob.status}</span>
                <h4 id="admin-job-review-title">{reviewJob.title}</h4>
                <p>{reviewJob.company_name} - {reviewJob.location}</p>
              </div>
              <button type="button" onClick={() => setReviewJob(null)} aria-label="Close job review">&times;</button>
            </div>

            <div className="admin-review-actions">
              {renderReviewActions(reviewJob)}
            </div>

            <div className="admin-review-grid">
              <div><span>Employer</span><strong>{reviewJob.employer_name ?? "Not provided"}</strong></div>
              <div><span>Employer Email</span><strong>{reviewJob.employer_email ?? "Not provided"}</strong></div>
              <div><span>Contact Email</span><strong>{reviewJob.verified_contact_email ?? reviewJob.contact_email ?? "Not provided"}</strong></div>
              <div><span>Applications</span><strong>{reviewJob.applications_count ?? 0}</strong></div>
              <div><span>Job Type</span><strong>{reviewJob.job_type ?? "Not provided"}</strong></div>
              <div><span>Workplace</span><strong>{reviewJob.workplace_type ?? "Not provided"}</strong></div>
              <div><span>Category</span><strong>{reviewJob.category ?? "Not provided"}</strong></div>
              <div><span>Salary</span><strong>{formatSalary(reviewJob)}</strong></div>
              <div><span>Experience</span><strong>{reviewJob.experience ?? "Not provided"}</strong></div>
              <div><span>Qualification</span><strong>{reviewJob.qualification ?? "Not provided"}</strong></div>
              <div><span>Career Level</span><strong>{reviewJob.career_level ?? "Not provided"}</strong></div>
              <div><span>Vacancies</span><strong>{reviewJob.vacancies ?? "Not provided"}</strong></div>
              <div><span>Deadline</span><strong>{formatDate(reviewJob.application_deadline)}</strong></div>
              <div><span>Created</span><strong>{formatDateTime(reviewJob.created_at)}</strong></div>
              <div><span>Published</span><strong>{formatDateTime(reviewJob.published_at)}</strong></div>
              <div><span>Skills</span><strong>{formatList(reviewJob.skills)}</strong></div>
            </div>

            {(reviewJob.company_website || reviewJob.application_url) && (
              <div className="admin-review-links">
                {reviewJob.company_website && <a href={reviewJob.company_website} target="_blank" rel="noreferrer">Company Website</a>}
                {reviewJob.application_url && <a href={reviewJob.application_url} target="_blank" rel="noreferrer">Application URL</a>}
              </div>
            )}

            <div className="admin-review-sections">
              {reviewSections.length > 0 ? reviewSections.map((section) => (
                <section key={section.title}>
                  <h5>{section.title}</h5>
                  <p>{section.body}</p>
                </section>
              )) : (
                <div className="admin-empty-state">No long-form job details were provided.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminPageShell>
  );
};

export default AdminJobs;
